import * as React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ElectionState, VoteResult, ElectionStatus, Routes } from '../../_generatedTypes.g';
import { turnoutPercentage } from '../../Election';
import { Translator, LanguageCode, getTranslator } from '../../Translator';
import { seatIcon, votedIcon, openIcon, closedIcon } from '../ui/icons';
import { ElectionAdminOptions } from '../Admin';
import { PopupMenu } from '../ui/PopupMenu';
import { EraseStatus } from '../../_generatedTypes.g';
import { duplicateElection, justAddedElectionCodeSet } from '../../features/home/homeSlice';
import { useAppSelector } from '../../hooks';

interface OwnProps {
    election: ElectionState,
    withAdminOptions: boolean,
    justAddedCode: string,
}

export const ElectionItem = ((ownProps: OwnProps) => {
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const language = useAppSelector(state => state.language);
    const elections = useAppSelector(state => state.elections);
    const hasCandidacy = useAppSelector(state => state.clientState.HasCandidacy);
    const dispatch = useDispatch();
    const t = getTranslator(language);
    const { election, withAdminOptions, justAddedCode } = ownProps;
    const turnout = turnoutPercentage(ownProps.election);
    const turnoutString = t.formatTurnout(turnout, t.localeCode, ' ' + t['Turnout']);


    React.useEffect(() => {
        if (justAddedCode === election.Code) {
            const elementToScrollTo = document.getElementById(`Home-Election-${election.ElectionId}`);
            if (elementToScrollTo) {
                //de timeout is een hack omdat scrollintoView blijkbaar niet werkt door timing
                window.setTimeout(() => elementToScrollTo.scrollIntoView({ behavior: 'smooth' }), 500);
            }
            dispatch(justAddedElectionCodeSet(''));
        }
    });

    return <div key={election.ElectionId} id={`Home-Election-${election.ElectionId}`}
        className={election.ActuallyInvisible ? 'home__election__container home__election__container--actually_invisible' : election.UserHasVotingRight ? 'home__election__container home__election__container' : 'home__election__container home__election__container--no-voting-right'}>
        {withAdminOptions && <div className="electionItem__optionsBar">
            {<PopupMenu title={t['Options']}><ElectionAdminOptions election={election} /></PopupMenu>}
            <button className="inlineEditButton home__election__duplicate" onClick={() => duplicateElection(election.Code, elections, hasCandidacy, token, dispatch)} title={t['Duplicate this election']}></button>
        </div>}
        <Link id={`Home-ElectionLink-${election.ElectionId}`} to={'/election/' + election.ElectionId + (election.CandidateSystem ? '/candidates' : '')} className="home__election"
            title={election.ActuallyInvisible
                ? t['This election is not visible to voters yet']
                : election.UserHasVotingRight
                    ? t['View this election']
                    : t['You are not a voter in this election']}>
            <ElectionLogo lang={t.languageCode}>{election}</ElectionLogo>
            {((): React.ReactElement => {
                switch (election.EraseStatus) {
                    case EraseStatus.Succeeded:
                        return <div className="statusbanner--ok">{t['Election was successfully erased']}</div>
                    case EraseStatus.Started:
                        return <div className="statusbanner--ok">{t['Election is being erased...']}</div>
                    case EraseStatus.FailedBecauseOpen:
                        return <div className="statusbanner--warning">{t['Could not erase this election because it is currently open']}</div>
                    case EraseStatus.FailedBecauseRemoved:
                        return <div className="statusbanner--warning">{t['Could not erase this election because it does not exist anymore']}</div>
                    case EraseStatus.FailedForUnknownReason:
                        return <div className="statusbanner--warning">{t['Erasing this election failed']}</div>
                    case EraseStatus.Idle:
                        return <></>
                }
            })()}
            <div className="turnout" title={turnoutString}>
                <div className="turnouttext">{turnoutString}</div>
                <div className="turnoutfill" style={{ width: `${turnout}%` }}></div>
            </div>
            <span title={t.translate(election.Name)} className="home__election__title">{t.translate(election.Name)}</span>
            {ElectionLabel(election, t)}
            <span className="home__election__label">
                {seatIcon}&nbsp;
                <span className="home__election__label__text">
                    {t['Seat count']}: {election.SeatCount}
                </span>
            </span>
        </Link>
    </div>;
});

export const ElectionLogo = ({ children: election, lang }: { children: ElectionState, lang: LanguageCode }) =>
    <>{(election.Logo != null || election.HasLogo) && <img
        className="home__election__image"
        src={election.Logo ? `data:image/svg+xml;base64,${election.Logo}` : Routes.Elections.Logo(election.ElectionId)}
        alt={election.Name[lang]} />}</>


const ElectionLabel = (election: ElectionState, t: Translator) => {
    if (election.VoteResult === VoteResult.Success) {
        return ElectionLabelWithIconAndText(votedIcon, t['Voted']);
    } else if (election.ElectionStatus === ElectionStatus.CandidacyPeriod) {
        return ElectionLabelWithIconAndText(openIcon, t['Candidacy']);
    } else if (election.ElectionStatus === ElectionStatus.Open) {
        return ElectionLabelWithIconAndText(openIcon, t['Open']);
    } else if (election.ElectionStatus === ElectionStatus.NotYetOpen) {
        return ElectionLabelWithIconAndText(closedIcon, t['Not yet open']);
    } else if (election.ElectionStatus === ElectionStatus.Closed) {
        return ElectionLabelWithIconAndText(closedIcon, t['Closed']);
    } else {
        return null;
    }
}

const ElectionLabelWithIconAndText = (icon: JSX.Element, text: string) => {
    return <span className="home__election__label">
        {icon} <span className="home__election__label__text">{text}</span>
    </span>;
}
